<template>
  <a-modal
      :visible="visible"
      :footer="null"
      :width="modalWidth"
      :centered="true"
      :destroyOnClose="true"
      @cancel="handleClose"
      wrapClassName="image-preview-modal"
  >
    <div class="image-preview-container">
      <!-- 图片加载中 -->
      <div v-if="loading" class="image-loading">
        <a-spin size="large" />
        <p>图片加载中...</p>
      </div>

      <!-- 图片加载失败 -->
      <div v-else-if="loadError" class="image-error">
        <a-icon type="picture" style="font-size: 48px; color: #ccc;" />
        <p>图片加载失败</p>
        <a-button @click="retryLoad">重新加载</a-button>
      </div>

      <!-- 图片展示 -->
      <div v-show="!loading && !loadError" class="image-wrapper">
        <img
            ref="previewImage"
            :src="imageUrl"
            :alt="alt"
            :style="imageStyle"
            @load="handleImageLoad"
            @error="handleImageError"
            @wheel="handleWheel"
            @mousedown="handleMouseDown"
        />
      </div>

      <!-- 隐藏的图片用于加载检测 -->
      <img
          v-if="loading"
          :src="imageUrl"
          style="display: none;"
          @load="handleImageLoad"
          @error="handleImageError"
      />

      <!-- 操作工具栏 -->
      <div v-if="!loading && !loadError" class="image-toolbar">
        <a-button-group>
          <a-button @click="zoomIn" :disabled="scale >= maxScale">
            <a-icon type="zoom-in" />
          </a-button>
          <a-button @click="zoomOut" :disabled="scale <= minScale">
            <a-icon type="zoom-out" />
          </a-button>
          <a-button @click="resetScale">
            <a-icon type="fullscreen" />
          </a-button>
          <a-button @click="rotateLeft">
            <a-icon type="undo" />
          </a-button>
          <a-button @click="rotateRight">
            <a-icon type="redo" />
          </a-button>
          <a-button @click="downloadImage" v-if="allowDownload">
            <a-icon type="download" />
          </a-button>
        </a-button-group>
      </div>
    </div>
  </a-modal>
</template>

<script>
export default {
  name: 'ImagePreview',
  props: {
    // 是否显示预览
    visible: {
      type: Boolean,
      default: false
    },
    // 图片URL
    imageUrl: {
      type: String,
      required: true
    },
    // 图片alt属性
    alt: {
      type: String,
      default: '预览图片'
    },
    // 是否允许下载
    allowDownload: {
      type: Boolean,
      default: true
    },
    // 最大缩放比例
    maxScale: {
      type: Number,
      default: 3
    },
    // 最小缩放比例
    minScale: {
      type: Number,
      default: 0.1
    }
  },
  data() {
    return {
      loading: true,
      loadError: false,
      scale: 1,
      rotation: 0,
      modalWidth: '80%',
      // 拖拽相关状态
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      translateX: 0,
      translateY: 0
    }
  },
  computed: {
    imageStyle() {
      return {
        transform: `scale(${this.scale}) rotate(${this.rotation}deg) translate(${this.translateX}px, ${this.translateY}px)`,
        transition: this.isDragging ? 'none' : 'transform 0.3s ease',
        maxWidth: '100%',
        maxHeight: '70vh',
        cursor: this.isDragging ? 'grabbing' : (this.scale > 1 ? 'grab' : 'default')
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetState()
      }
    },
    imageUrl() {
      if (this.visible) {
        this.resetState()
      }
    }
  },
  methods: {
    // 重置状态
    resetState() {
      this.loading = true
      this.loadError = false
      this.scale = 1
      this.rotation = 0
      this.translateX = 0
      this.translateY = 0
      this.isDragging = false
    },

    // 图片加载完成
    handleImageLoad() {
      this.loading = false
      this.loadError = false
    },

    // 图片加载失败
    handleImageError() {
      this.loading = false
      this.loadError = true
    },

    // 重新加载
    retryLoad() {
      this.resetState()
      // 强制重新加载图片
      this.$nextTick(() => {
        if (this.$refs.previewImage) {
          this.$refs.previewImage.src = this.imageUrl + '?t=' + Date.now()
        }
      })
    },

    // 关闭预览
    handleClose() {
      this.$emit('close')
    },

    // 放大
    zoomIn() {
      if (this.scale < this.maxScale) {
        this.scale = Math.min(this.scale + 0.2, this.maxScale)
      }
    },

    // 缩小
    zoomOut() {
      if (this.scale > this.minScale) {
        this.scale = Math.max(this.scale - 0.2, this.minScale)
      }
    },

    // 重置缩放
    resetScale() {
      this.scale = 1
      this.rotation = 0
      this.translateX = 0
      this.translateY = 0
    },

    // 向左旋转
    rotateLeft() {
      this.rotation -= 90
    },

    // 向右旋转
    rotateRight() {
      this.rotation += 90
    },

    // 鼠标滚轮缩放
    handleWheel(event) {
      event.preventDefault()
      const delta = event.deltaY
      if (delta < 0) {
        this.zoomIn()
      } else {
        this.zoomOut()
      }
    },

    // 下载图片
    downloadImage() {
      try {
        const link = document.createElement('a')
        link.href = this.imageUrl
        link.download = this.alt || 'image'
        link.target = '_blank'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('下载失败:', error)
        this.$message.error('下载失败')
      }
    },

    // 鼠标按下开始拖拽
    handleMouseDown(event) {
      if (this.scale <= 1) return // 只有放大时才允许拖拽

      this.isDragging = true
      this.dragStartX = event.clientX - this.translateX
      this.dragStartY = event.clientY - this.translateY

      // 添加全局鼠标事件监听
      document.addEventListener('mousemove', this.handleMouseMove)
      document.addEventListener('mouseup', this.handleMouseUp)

      event.preventDefault()
    },

    // 鼠标移动拖拽
    handleMouseMove(event) {
      if (!this.isDragging) return

      this.translateX = event.clientX - this.dragStartX
      this.translateY = event.clientY - this.dragStartY

      event.preventDefault()
    },

    // 鼠标释放结束拖拽
    handleMouseUp(event) {
      this.isDragging = false

      // 移除全局鼠标事件监听
      document.removeEventListener('mousemove', this.handleMouseMove)
      document.removeEventListener('mouseup', this.handleMouseUp)

      event.preventDefault()
    }
  },

  // 组件销毁时清理事件监听器
  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleMouseMove)
    document.removeEventListener('mouseup', this.handleMouseUp)
  }
}
</script>

<style lang="less" scoped>
.image-preview-container {
  position: relative;
  text-align: center;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.image-loading,
.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  color: #666;

  p {
    margin: 16px 0;
    font-size: 14px;
  }
}

.image-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 70vh;
  overflow: hidden;
  user-select: none;

  img {
    display: block;
    user-select: none;
    -webkit-user-drag: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;

    &:active {
      cursor: grabbing;
    }
  }
}

.image-toolbar {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  border-radius: 6px;
  padding: 8px;

  .ant-btn {
    color: #fff;
    border-color: transparent;
    background: transparent;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: transparent;
      color: #fff;
    }

    &:disabled {
      color: rgba(255, 255, 255, 0.3);
      background: transparent;
      border-color: transparent;
    }
  }
}
</style>

<style lang="less">
.image-preview-modal {
  .ant-modal {
    max-width: 90vw;
    background: transparent;
    box-shadow: none;
    position: relative;
  }

  .ant-modal-content {
    background: transparent;
    box-shadow: none;
    position: relative;
  }

  .ant-modal-body {
    padding: 0;
    background: transparent;
  }

  .ant-modal-close {
    position: fixed !important;
    color: #fff !important;
    background: rgba(0, 0, 0, 0.5) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
    border-radius: 4px !important;
    width: 32px !important;
    height: 32px !important;
    line-height: 30px !important;
    top: 20px !important;
    right: 20px !important;
    z-index: 9999 !important;
    font-size: 16px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition: none !important;
    transform: none !important;
    margin: 0 !important;
    left: auto !important;
    bottom: auto !important;

    &:hover {
      background: rgba(0, 0, 0, 0.7) !important;
      border-color: rgba(255, 255, 255, 0.5) !important;
      color: #fff !important;
      transform: none !important;
    }

    &:focus {
      background: rgba(0, 0, 0, 0.5) !important;
      border-color: rgba(255, 255, 255, 0.3) !important;
      color: #fff !important;
      transform: none !important;
    }

    .anticon {
      font-size: 14px !important;
      transform: none !important;
    }
  }

  .ant-modal-mask {
    background: rgba(0, 0, 0, 0.8);
  }

  // 确保关闭按钮不受其他样式影响
  .ant-modal-wrap {
    position: relative;
  }
}
</style>
